import axios, {type InternalAxiosRequestConfig} from "axios";

import eventBus from "@/app/events/eventBus";

declare module "axios" {
    export interface AxiosRequestConfig {
        // Requisições públicas (ex.: login) não devem levar o Bearer token.
        semToken?: boolean;
    }
}

/**
 * Instância única do axios. Antes o token era remontado à mão em cada chamada do
 * request.helper e o 401 não tinha tratamento nenhum: a sessão expirava e a tela
 * ficava mostrando erro de permissão sem mandar o usuário para o login.
 */
const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (!config.semToken) {
        const token = localStorage.getItem("userToken") ?? "";
        config.headers.set("Authorization", "Bearer " + token);
    }

    // Upload (Solicitação Geral): o Content-Type padrão da instância impediria o axios
    // de montar o boundary do multipart. Tirar o header é o que o helper antigo fazia
    // ao usar um objeto de headers só com Authorization.
    if (config.data instanceof FormData) {
        config.headers.delete("Content-Type");
    }

    return config;
});

// Evita disparar logout/redirect em duplicidade quando várias requisições
// em voo recebem 401 ao mesmo tempo (token expirado no meio da sessão).
let tratandoSessaoExpirada = false;

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {

        if (error.response?.status === 401 && !tratandoSessaoExpirada) {
            tratandoSessaoExpirada = true;

            eventBus.emit("system:requestError", {
                success: false,
                data: "Sessão expirada. Faça login novamente.",
                message: "",
                auxData: null,
            });

            // Import dinâmico de propósito: evita o ciclo estático
            // apiClient -> auth.store -> auth.service -> request.helper -> apiClient.
            const {useAuthStore} = await import("@/stores/auth.store");
            useAuthStore().sair();

            const router = (await import("@/router")).default;
            router.push("/signin").finally(() => {
                tratandoSessaoExpirada = false;
            });
        }

        return Promise.reject(error);
    }
);

export default apiClient;
